import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Columns3,
  Copy,
  ExternalLink,
  FileText,
  Grid3X3,
  Link2,
  Link2Off,
  List,
  Loader2,
  MapPin,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Stethoscope,
  Trash2,
  User,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import { auth } from "../firebase/client";
import { usePatient } from "../contexts/PatientContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Appointment,
  AppointmentInput,
  AppointmentType,
  EVENT_COLORS,
  EventColor,
  TYPE_DEFAULT_COLOR,
  createAppointment,
  deleteAppointment,
  durationLabel,
  endOfMonth,
  formatDate,
  formatDateShort,
  formatTime,
  getEventColorStyle,
  getMonthGrid,
  getTimeSlots,
  getWeekDays,
  isSameDay,
  startOfMonth,
  subscribeToAppointments,
  updateAppointment,
} from "../services/agendaService";
import { getAppointmentDisplayNotes } from "../utils/googleCalendarText";
import {
  fetchGoogleCalendarStatus,
  openGoogleOAuthPopup,
  disconnectGoogleCalendar,
  syncGoogleCalendarNow,
  syncCreateEvent,
  syncDeleteEvent,
  type GoogleCalendarStatus,
} from "../services/googleCalendarClient";
import type { Patient } from "../contexts/types";
import { AppTab } from "../contexts/types";

// ─── Constants ────────────────────────────────────────────────────────────────

const APPOINTMENT_TYPES: AppointmentType[] = [
  "Consulta", "Cirurgia", "Follow-up", "Pré-op", "Retorno", "Urgência", "Outro",
];

const DURATION_OPTIONS = [
  { label: "30 min", value: 30 },
  { label: "45 min", value: 45 },
  { label: "1 hora", value: 60 },
  { label: "1h 30min", value: 90 },
  { label: "2 horas", value: 120 },
  { label: "Bloco cirúrgico (4h)", value: 240 },
];

const MONTH_NAMES = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];

const WEEKDAY_SHORT = ["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"];

type CalendarView = "month" | "week" | "day" | "list";

// ─── Sub-components ───────────────────────────────────────────────────────────

function EventChip({ apt, onClick, onChipEnter, onChipLeave }: {
  apt: Appointment;
  onClick: () => void;
  onChipEnter: (apt: Appointment, rect: DOMRect) => void;
  onChipLeave: () => void;
}) {
  const col = getEventColorStyle(apt.color);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={(e) => onChipEnter(apt, (e.currentTarget as HTMLElement).getBoundingClientRect())}
      onMouseLeave={onChipLeave}
      onTouchStart={(e) => { e.stopPropagation(); onChipEnter(apt, (e.currentTarget as HTMLElement).getBoundingClientRect()); }}
      className={`w-full text-left text-[11px] font-medium truncate px-1.5 py-0.5 rounded ${col.bg} ${col.text} border ${col.border} hover:opacity-90 transition-opacity`}
    >
      {formatTime(apt.startDate)} {apt.patientName}
    </button>
  );
}

function ColorDot({ color, size = 8 }: { color: EventColor; size?: number }) {
  return (
    <span
      className={`inline-block rounded-full ${getEventColorStyle(color).dot}`}
      style={{ width: size, height: size, flexShrink: 0 }}
    />
  );
}

function GoogleBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded-full border border-slate-700">
      <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Google
    </span>
  );
}

// ─── Event Tooltip ────────────────────────────────────────────────────────────

/** Calculates the best position so tooltip never overflows the viewport. */
function computeTooltipPosition(
  anchor: DOMRect,
  tooltipW: number,
  tooltipH: number
): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MARGIN = 10;
  const clampTop = (t: number) => Math.max(MARGIN, Math.min(t, vh - tooltipH - MARGIN));

  // Prefer right
  if (anchor.right + MARGIN + tooltipW <= vw) {
    return { left: anchor.right + MARGIN, top: clampTop(anchor.top) };
  }
  // Try left
  if (anchor.left - MARGIN - tooltipW >= 0) {
    return { left: anchor.left - MARGIN - tooltipW, top: clampTop(anchor.top) };
  }
  // Try below (centered under chip)
  const cx = Math.max(MARGIN, Math.min(
    anchor.left + anchor.width / 2 - tooltipW / 2,
    vw - tooltipW - MARGIN
  ));
  if (anchor.bottom + MARGIN + tooltipH <= vh) {
    return { left: cx, top: anchor.bottom + MARGIN };
  }
  // Fallback: above
  return { left: cx, top: Math.max(MARGIN, anchor.top - MARGIN - tooltipH) };
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <div className="flex-1 h-px bg-slate-800" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">{label}</span>
      <div className="flex-1 h-px bg-slate-800" />
    </div>
  );
}

function TooltipRow({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs text-slate-300">
      <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" />
      <span className="leading-tight">{children}</span>
    </div>
  );
}

function EventTooltip({ apt, rect, patient, onTooltipEnter, onTooltipLeave, onNavigateToPatient }: {
  apt: Appointment;
  rect: DOMRect;
  patient: Patient | null;
  onTooltipEnter: () => void;
  onTooltipLeave: () => void;
  onNavigateToPatient: (id: string) => void;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = tooltipRef.current;
    setPos(computeTooltipPosition(rect, w, h));
    // Small tick so CSS transition fires
    requestAnimationFrame(() => setVisible(true));
  }, [rect]);

  const col = getEventColorStyle(apt.color);
  const duration = durationLabel(apt.startDate, apt.endDate);
  const displayNotes = getAppointmentDisplayNotes(apt);
  const isGoogleEvent = apt.origin === "google" || Boolean(apt.googleEventId);

  // Derive patient initials
  const initials = useMemo(() => {
    const name = patient?.name || apt.patientName || "";
    return name.split(" ").map((n) => n[0]).filter(Boolean).join("").slice(0, 2).toUpperCase() || "?";
  }, [patient?.name, apt.patientName]);

  // All alerts: from apt + from patient fields
  const allAlerts = useMemo(() => {
    const set = new Set<string>();
    (apt.patientAlerts || []).forEach((a) => set.add(a));
    (patient?.allergies || []).forEach((a) => set.add(a));
    (patient?.comorbidities || []).forEach((c) => set.add(c));
    return Array.from(set).filter(Boolean);
  }, [apt.patientAlerts, patient?.allergies, patient?.comorbidities]);

  const statusLabel: Record<string, string> = {
    "Pre-op": "Pré-operatório",
    "Post-op": "Pós-operatório",
    "Urgency": "Urgência",
  };

  const style: React.CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    width: 316,
    opacity: visible && pos ? 1 : 0,
    transform: visible && pos ? "translateY(0)" : "translateY(6px)",
    transition: "opacity 150ms ease, transform 150ms ease",
    ...(pos ?? { left: -9999, top: -9999 }),
  };

  return (
    <div
      ref={tooltipRef}
      style={style}
      className="bg-slate-900 border border-slate-700/80 rounded-xl shadow-2xl shadow-black/70 overflow-hidden pointer-events-auto"
      onMouseEnter={onTooltipEnter}
      onMouseLeave={onTooltipLeave}
    >
      {/* Colored top bar */}
      <div className={`h-1 w-full ${col.dot}`} />

      <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">

        {/* ── Patient header ── */}
        {apt.patientId ? (
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${col.bg} border ${col.border} shrink-0`}>
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white leading-tight truncate">
                {patient?.name || apt.patientName}
              </p>
              {(patient?.diagnosis || apt.patientDiagnosis) && (
                <p className="text-xs text-slate-400 truncate mt-0.5">
                  {patient?.diagnosis || apt.patientDiagnosis}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0">
              <Calendar className="h-4 w-4 text-slate-500" />
            </div>
            <p className="text-sm font-bold text-white">{apt.patientName || "Evento externo"}</p>
          </div>
        )}

        {/* ── Event section ── */}
        <SectionDivider label="Evento" />
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${col.bg} ${col.text}`}>
              {apt.type}
            </span>
          </div>
          <TooltipRow icon={Calendar}>{formatDate(apt.startDate)}</TooltipRow>
          <TooltipRow icon={Clock}>
            {formatTime(apt.startDate)} — {formatTime(apt.endDate)}
            <span className="text-slate-500 ml-1">({duration})</span>
          </TooltipRow>
          {apt.location && <TooltipRow icon={MapPin}>{apt.location}</TooltipRow>}
          {isGoogleEvent ? (
            <TooltipRow icon={FileText}>
              <span className="text-slate-400 whitespace-pre-line">{displayNotes || "Sem descrição"}</span>
            </TooltipRow>
          ) : (
            apt.notes && (
              <TooltipRow icon={FileText}>
                <span className="text-slate-400 italic whitespace-pre-line">{apt.notes}</span>
              </TooltipRow>
            )
          )}
        </div>

        {/* ── Patient section ── */}
        {apt.patientId && (
          <>
            <SectionDivider label="Paciente" />
            <div className="space-y-2">
              {patient?.age && (
                <TooltipRow icon={User}>
                  {patient.name} · {patient.age} anos
                </TooltipRow>
              )}
              {patient?.status && (
                <TooltipRow icon={Stethoscope}>
                  {statusLabel[patient.status] ?? patient.status}
                </TooltipRow>
              )}
              {patient?.phone && (
                <TooltipRow icon={Phone}>{patient.phone}</TooltipRow>
              )}
            </div>
          </>
        )}

        {/* ── Clinical alerts ── */}
        {allAlerts.length > 0 && (
          <div className="rounded-lg bg-amber-500/12 border border-amber-500/30 px-3 py-2.5 space-y-1">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400">Alerta clínico</span>
            </div>
            {allAlerts.map((a, i) => (
              <p key={i} className="text-xs text-amber-200 leading-snug pl-5">• {a}</p>
            ))}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="pt-1 border-t border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            {apt.origin === "google" ? (
              <GoogleBadge />
            ) : (
              <span className="text-[10px] text-slate-600">OrthoPrecision</span>
            )}
            {apt.syncGoogle && apt.origin === "orthoprecision" && (
              <span className="flex items-center gap-1 text-[10px] text-cyan-500">
                <RefreshCw className="h-3 w-3" />
                Sincronizado
              </span>
            )}
          </div>

          {apt.patientId && (
            <button
              onClick={() => onNavigateToPatient(apt.patientId!)}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 font-semibold py-1.5 rounded-lg hover:bg-cyan-500/10 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Ver prontuário
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Month View ───────────────────────────────────────────────────────────────

function MonthView({ year, month, today, appointments, onDayClick, onEventClick, onChipEnter, onChipLeave }: {
  year: number;
  month: number;
  today: Date;
  appointments: Appointment[];
  onDayClick: (date: Date) => void;
  onEventClick: (apt: Appointment) => void;
  onChipEnter: (apt: Appointment, rect: DOMRect) => void;
  onChipLeave: () => void;
}) {
  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const isCurrentMonth = (d: Date) => d.getMonth() === month;

  const eventsForDay = (d: Date) =>
    appointments.filter((a) => isSameDay(new Date(a.startDate), d));

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-slate-800">
        {WEEKDAY_SHORT.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 grid grid-cols-7 grid-rows-6 overflow-y-auto">
        {grid.map((d, i) => {
          const dayApts = eventsForDay(d);
          const isToday = isSameDay(d, today);
          const isCurrent = isCurrentMonth(d);
          const MAX_VISIBLE = 3;
          const visible = dayApts.slice(0, MAX_VISIBLE);
          const overflow = dayApts.length - MAX_VISIBLE;

          return (
            <div
              key={i}
              onClick={() => onDayClick(d)}
              className={`border-r border-b border-slate-800/60 p-1.5 cursor-pointer hover:bg-slate-800/30 transition-colors min-h-[80px] ${!isCurrent ? "opacity-35" : ""}`}
            >
              <div className="flex justify-end mb-1">
                <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full font-medium ${isToday ? "bg-cyan-500 text-slate-950 font-bold" : "text-slate-400"}`}>
                  {d.getDate()}
                </span>
              </div>
              <div className="space-y-0.5">
                {visible.map((apt) => (
                  <EventChip key={apt.id} apt={apt} onClick={() => onEventClick(apt)} onChipEnter={onChipEnter} onChipLeave={onChipLeave} />
                ))}
                {overflow > 0 && (
                  <button className="text-[10px] text-slate-500 hover:text-slate-300 w-full text-left px-1">
                    +{overflow} mais
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week View ────────────────────────────────────────────────────────────────

function WeekView({ refDate, today, appointments, onSlotClick, onEventClick, onChipEnter, onChipLeave }: {
  refDate: Date;
  today: Date;
  appointments: Appointment[];
  onSlotClick: (date: Date) => void;
  onEventClick: (apt: Appointment) => void;
  onChipEnter: (apt: Appointment, rect: DOMRect) => void;
  onChipLeave: () => void;
}) {
  const days = useMemo(() => getWeekDays(refDate), [refDate]);
  const slots = getTimeSlots();
  const SLOT_H = 52; // px per hour slot

  const eventsForDay = (d: Date) =>
    appointments.filter((a) => isSameDay(new Date(a.startDate), d));

  const eventStyle = (apt: Appointment): React.CSSProperties => {
    const start = new Date(apt.startDate);
    const end = new Date(apt.endDate);
    const startH = start.getHours() + start.getMinutes() / 60;
    const endH = end.getHours() + end.getMinutes() / 60;
    const top = (startH - 7) * SLOT_H;
    const height = Math.max((endH - startH) * SLOT_H, 22);
    return { position: "absolute", top, height, left: 2, right: 2 };
  };

  return (
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="grid sticky top-0 z-10 bg-slate-950 border-b border-slate-800" style={{ gridTemplateColumns: "56px repeat(7,1fr)" }}>
        <div />
        {days.map((d, i) => {
          const isToday = isSameDay(d, today);
          return (
            <div key={i} className="text-center py-2 border-l border-slate-800/60">
              <p className="text-xs text-slate-500">{WEEKDAY_SHORT[i]}</p>
              <p className={`text-sm font-semibold mt-0.5 w-7 h-7 flex items-center justify-center mx-auto rounded-full ${isToday ? "bg-cyan-500 text-slate-950" : "text-slate-300"}`}>
                {d.getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7,1fr)" }}>
        {/* Time labels */}
        <div>
          {slots.map((s) => (
            <div key={s} style={{ height: SLOT_H }} className="border-b border-slate-800/30 flex items-start justify-end pr-2 pt-1">
              <span className="text-[10px] text-slate-600">{s}</span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        {days.map((d, di) => (
          <div
            key={di}
            className="relative border-l border-slate-800/60"
            style={{ height: slots.length * SLOT_H }}
            onClick={() => {
              const dt = new Date(d);
              dt.setHours(9, 0, 0, 0);
              onSlotClick(dt);
            }}
          >
            {/* Hour lines */}
            {slots.map((_, si) => (
              <div key={si} style={{ position: "absolute", top: si * SLOT_H, left: 0, right: 0, height: SLOT_H }} className="border-b border-slate-800/20" />
            ))}

            {/* Events */}
            {eventsForDay(d).map((apt) => {
              const col = getEventColorStyle(apt.color);
              return (
                <button
                  key={apt.id}
                  style={eventStyle(apt)}
                  onClick={(e) => { e.stopPropagation(); onEventClick(apt); }}
                  onMouseEnter={(e) => onChipEnter(apt, (e.currentTarget as HTMLElement).getBoundingClientRect())}
                  onMouseLeave={onChipLeave}
                  className={`${col.bg} ${col.text} border ${col.border} rounded-md text-left text-[11px] px-1.5 py-1 hover:opacity-90 overflow-hidden`}
                >
                  <p className="font-semibold truncate">{apt.patientName}</p>
                  <p className="opacity-80 truncate">{apt.type}</p>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Day View ─────────────────────────────────────────────────────────────────

function DayView({ date, today, appointments, onSlotClick, onEventClick, onChipEnter, onChipLeave }: {
  date: Date;
  today: Date;
  appointments: Appointment[];
  onSlotClick: (date: Date) => void;
  onEventClick: (apt: Appointment) => void;
  onChipEnter: (apt: Appointment, rect: DOMRect) => void;
  onChipLeave: () => void;
}) {
  const slots = getTimeSlots();
  const SLOT_H = 60;
  const dayApts = appointments.filter((a) => isSameDay(new Date(a.startDate), date));
  const isToday = isSameDay(date, today);

  const aptStyle = (apt: Appointment): React.CSSProperties => {
    const s = new Date(apt.startDate), e = new Date(apt.endDate);
    const sh = s.getHours() + s.getMinutes() / 60;
    const eh = e.getHours() + e.getMinutes() / 60;
    return { position: "absolute", top: (sh - 7) * SLOT_H, height: Math.max((eh - sh) * SLOT_H, 28), left: 8, right: 8 };
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="sticky top-0 z-10 bg-slate-950 border-b border-slate-800 px-6 py-3 flex items-center gap-3">
        <span className={`text-3xl font-bold ${isToday ? "text-cyan-400" : "text-white"}`}>
          {date.getDate()}
        </span>
        <div>
          <p className="text-sm font-medium text-slate-300">
            {["Domingo","Segunda","Terça","Quarta","Quinta","Sexta","Sábado"][date.getDay()]}
          </p>
          <p className="text-xs text-slate-500">
            {MONTH_NAMES[date.getMonth()]} {date.getFullYear()}
          </p>
        </div>
        <span className="ml-auto text-xs text-slate-500">{dayApts.length} evento{dayApts.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="flex">
        {/* Time labels */}
        <div className="w-16 shrink-0">
          {slots.map((s) => (
            <div key={s} style={{ height: SLOT_H }} className="border-b border-slate-800/20 flex items-start justify-end pr-3 pt-1">
              <span className="text-[11px] text-slate-600">{s}</span>
            </div>
          ))}
        </div>

        {/* Event column */}
        <div className="flex-1 relative border-l border-slate-800/40" style={{ height: slots.length * SLOT_H }}
          onClick={() => { const d = new Date(date); d.setHours(9, 0, 0, 0); onSlotClick(d); }}>
          {slots.map((_, i) => (
            <div key={i} style={{ position: "absolute", top: i * SLOT_H, left: 0, right: 0, height: SLOT_H }} className="border-b border-slate-800/15" />
          ))}
          {dayApts.map((apt) => {
            const col = getEventColorStyle(apt.color);
            return (
              <button
                key={apt.id}
                style={aptStyle(apt)}
                onClick={(e) => { e.stopPropagation(); onEventClick(apt); }}
                onMouseEnter={(e) => onChipEnter(apt, (e.currentTarget as HTMLElement).getBoundingClientRect())}
                onMouseLeave={onChipLeave}
                className={`${col.bg} border-l-4 ${col.border} rounded-r-lg text-left px-3 py-2 hover:opacity-90`}
              >
                <p className={`text-sm font-semibold ${col.text}`}>{apt.patientName}</p>
                <p className="text-xs text-slate-400">{apt.type} · {formatTime(apt.startDate)} – {formatTime(apt.endDate)}</p>
                {apt.location && <p className="text-xs text-slate-500 mt-0.5">{apt.location}</p>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────

function ListView({ appointments, onEventClick }: {
  appointments: Appointment[];
  onEventClick: (apt: Appointment) => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    [...appointments].sort((a, b) => a.startDate - b.startDate).forEach((apt) => {
      const key = new Date(apt.startDate).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(apt);
    });
    return Array.from(map.entries());
  }, [appointments]);

  if (grouped.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-20 text-slate-500">
        <Calendar className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm">Nenhum evento neste período.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-6">
      {grouped.map(([dateStr, apts]) => (
        <div key={dateStr}>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
            {formatDate(new Date(dateStr).getTime())}
          </p>
          <div className="space-y-2">
            {apts.map((apt) => {
              const col = getEventColorStyle(apt.color);
              return (
                <button
                  key={apt.id}
                  onClick={() => onEventClick(apt)}
                  className="w-full flex items-center gap-4 bg-slate-900/50 border border-slate-800 rounded-xl px-4 py-3 hover:border-slate-700 text-left transition-colors"
                >
                  <ColorDot color={apt.color} size={10} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{apt.patientName}</p>
                    <p className="text-xs text-slate-400">{apt.type}{apt.location ? ` · ${apt.location}` : ""}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-300">{formatTime(apt.startDate)}</p>
                    <p className="text-xs text-slate-500">{durationLabel(apt.startDate, apt.endDate)}</p>
                  </div>
                  {apt.origin === "google" && <GoogleBadge />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Mini Calendar (Sidebar) ──────────────────────────────────────────────────

function MiniCalendar({ year, month, selectedDate, appointments, onDaySelect, onNavigate }: {
  year: number; month: number; selectedDate: Date; appointments: Appointment[];
  onDaySelect: (d: Date) => void; onNavigate: (delta: number) => void;
}) {
  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const today = new Date();
  const hasEvent = (d: Date) => appointments.some((a) => isSameDay(new Date(a.startDate), d));

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => onNavigate(-1)} className="p-1 hover:bg-slate-800 rounded">
          <ChevronLeft className="h-4 w-4 text-slate-400" />
        </button>
        <span className="text-sm font-medium text-slate-300">
          {MONTH_NAMES[month].slice(0, 3)} {year}
        </span>
        <button onClick={() => onNavigate(1)} className="p-1 hover:bg-slate-800 rounded">
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0">
        {["S","T","Q","Q","S","S","D"].map((d, i) => (
          <div key={i} className="text-center text-[10px] text-slate-600 font-medium pb-1">{d}</div>
        ))}
        {grid.map((d, i) => {
          const isCur = d.getMonth() === month;
          const isT = isSameDay(d, today);
          const isSel = isSameDay(d, selectedDate);
          const hasEv = hasEvent(d);
          return (
            <button
              key={i}
              onClick={() => onDaySelect(d)}
              className={`relative flex flex-col items-center justify-center w-7 h-7 mx-auto rounded-full text-[11px] transition-colors
                ${isSel ? "bg-cyan-500 text-slate-950 font-bold" : isT ? "ring-1 ring-cyan-500 text-cyan-400" : isCur ? "text-slate-300 hover:bg-slate-800" : "text-slate-700"}`}
            >
              {d.getDate()}
              {hasEv && !isSel && (
                <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-cyan-400" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── 3-Step Create Event Modal ────────────────────────────────────────────────

type ModalStep = 1 | 2 | 3 | "success";

interface EventForm {
  patientId: string | null;
  patientName: string;
  patientDiagnosis: string;
  patientAlerts: string[];
  type: AppointmentType;
  date: string;   // YYYY-MM-DD
  time: string;   // HH:MM
  duration: number;
  location: string;
  notes: string;
  color: EventColor;
  syncGoogle: boolean;
  notifyPatient: boolean;
}

function defaultForm(prefillDate?: Date): EventForm {
  const d = prefillDate || new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    patientId: null, patientName: "", patientDiagnosis: "", patientAlerts: [],
    type: "Consulta",
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: "09:00",
    duration: 60,
    location: "", notes: "",
    color: "blue",
    syncGoogle: true, notifyPatient: true,
  };
}

function StepIndicator({ step }: { step: ModalStep }) {
  const steps = [
    { n: 1, label: "Paciente" },
    { n: 2, label: "Horário" },
    { n: 3, label: "Confirmar" },
  ];
  const current = typeof step === "number" ? step : 3;
  return (
    <div className="flex items-center justify-center gap-2 py-4 border-b border-slate-800">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-1.5">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors
              ${current > s.n ? "bg-emerald-500 text-white" : current === s.n ? "bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-500"}`}>
              {current > s.n ? <Check className="h-3 w-3" /> : s.n}
            </div>
            <span className={`text-xs ${current === s.n ? "text-white font-medium" : "text-slate-500"}`}>{s.label}</span>
          </div>
          {i < steps.length - 1 && <div className="w-8 h-px bg-slate-800" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function PatientSearchStep({ patients, form, onSelect, onNext }: {
  patients: Patient[];
  form: EventForm;
  onSelect: (p: Patient | null) => void;
  onNext: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return patients.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.diagnosis || "").toLowerCase().includes(q) ||
        (p.anatomy || "").toLowerCase().includes(q)
    );
  }, [patients, query]);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar por nome, diagnóstico..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">{patients.length} pacientes cadastrados</p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {filtered.map((p) => {
          const selected = form.patientId === p.id;
          const hasAlert = (p.allergies?.length ?? 0) > 0 || (p.comorbidities?.length ?? 0) > 0;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all
                ${selected ? "border-cyan-500/60 bg-cyan-500/10" : "border-slate-800 bg-slate-900/50 hover:border-slate-700"}`}
            >
              <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-white shrink-0">
                {p.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-white truncate">{p.name}</p>
                  {hasAlert && <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
                  {selected && <Check className="h-3.5 w-3.5 text-cyan-400 shrink-0 ml-auto" />}
                </div>
                <p className="text-xs text-slate-400 truncate">{p.diagnosis || p.anatomy || "Sem diagnóstico"}</p>
                <div className="flex items-center gap-2 mt-1">
                  {p.age && <span className="text-[10px] text-slate-500">{p.age} anos</span>}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.status === "Urgency" ? "bg-red-500/20 text-red-300" : "bg-slate-800 text-slate-400"}`}>
                    {p.status}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="py-8 text-center text-slate-500 text-sm">
            Nenhum paciente encontrado.
          </div>
        )}
      </div>

      <div className="p-4 border-t border-slate-800">
        <button
          onClick={onNext}
          disabled={!form.patientId}
          className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-slate-950 font-bold py-2.5 rounded-xl text-sm transition-colors"
        >
          Próximo →
        </button>
      </div>
    </div>
  );
}

function TimeAndTypeStep({ form, onChange, onBack, onNext }: {
  form: EventForm;
  onChange: (updates: Partial<EventForm>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const hasAlerts = form.patientAlerts.length > 0;
  const COLORS: EventColor[] = ["blue", "green", "purple", "amber", "red"];

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Selected patient card */}
        <div className="flex items-center gap-3 bg-slate-800/60 border border-slate-700 rounded-xl p-3">
          <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-white">
            {form.patientName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">{form.patientName}</p>
            <p className="text-xs text-slate-400 truncate">{form.patientDiagnosis || "Sem diagnóstico"}</p>
          </div>
        </div>

        {/* Clinical alert */}
        {hasAlerts && (
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-300 mb-0.5">Alerta clínico</p>
              <p className="text-xs text-amber-200">{form.patientAlerts.join(" · ")}</p>
            </div>
          </div>
        )}

        {/* Date + Time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Data</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => onChange({ date: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Horário</label>
            <input
              type="time"
              value={form.time}
              onChange={(e) => onChange({ time: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
            />
          </div>
        </div>

        {/* Type + Duration */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Tipo</label>
            <select
              value={form.type}
              onChange={(e) => {
                const t = e.target.value as AppointmentType;
                onChange({ type: t, color: TYPE_DEFAULT_COLOR[t] });
              }}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
            >
              {APPOINTMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Duração</label>
            <select
              value={form.duration}
              onChange={(e) => onChange({ duration: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
            >
              {DURATION_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-xs text-slate-400 mb-1.5">Local</label>
          <input
            type="text"
            placeholder="Ex: Consultório 1, Centro Cirúrgico..."
            value={form.location}
            onChange={(e) => onChange({ location: e.target.value })}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-slate-400 mb-1.5">Observações</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="Informações adicionais..."
            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 resize-none"
          />
        </div>

        {/* Color */}
        <div>
          <label className="block text-xs text-slate-400 mb-2">Cor do evento</label>
          <div className="flex gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => onChange({ color: c })}
                className={`w-7 h-7 rounded-full ${EVENT_COLORS[c].dot} transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110" : "opacity-60 hover:opacity-100"}`}
              />
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="space-y-2 pt-1">
          {([
            ["syncGoogle", "Sincronizar com Google Agenda"],
            ["notifyPatient", "Notificar paciente por e-mail"],
          ] as const).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-slate-800/60">
              <span className="text-sm text-slate-300">{label}</span>
              <button
                onClick={() => onChange({ [key]: !form[key] } as any)}
                className={`w-10 h-5 rounded-full transition-colors relative ${form[key] ? "bg-cyan-500" : "bg-slate-700"}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form[key] ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 border-t border-slate-800 flex gap-3">
        <button onClick={onBack} className="flex-1 border border-slate-700 text-slate-300 font-semibold py-2.5 rounded-xl text-sm hover:bg-slate-800 transition-colors">
          ← Voltar
        </button>
        <button onClick={onNext} className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold py-2.5 rounded-xl text-sm transition-colors">
          Próximo →
        </button>
      </div>
    </div>
  );
}

function ReviewStep({ form, onBack, onConfirm, saving }: {
  form: EventForm; onBack: () => void; onConfirm: () => void; saving: boolean;
}) {
  const col = getEventColorStyle(form.color);
  const [startMs] = useMemo(() => {
    const [y, mo, d] = form.date.split("-").map(Number);
    const [h, mi] = form.time.split(":").map(Number);
    const start = new Date(y, mo - 1, d, h, mi);
    return [start.getTime()];
  }, [form.date, form.time]);

  const actions = [
    "Salvar na agenda do OrthoPrecision",
    form.syncGoogle && "Sincronizar com Google Agenda",
    form.notifyPatient && "Notificar paciente por e-mail",
    "Registrar no prontuário do paciente",
  ].filter(Boolean) as string[];

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Summary card */}
        <div className={`border-l-4 ${col.border} bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-2`}>
          <div className="flex items-center gap-2">
            <ColorDot color={form.color} />
            <span className={`text-xs font-bold ${col.text}`}>{form.type}</span>
          </div>
          <p className="text-lg font-bold text-white">{form.patientName}</p>
          {form.patientDiagnosis && <p className="text-sm text-slate-400">{form.patientDiagnosis}</p>}
          <div className="pt-1 space-y-1.5 text-sm text-slate-300">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span>{formatDate(startMs)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              <span>{form.time} · {DURATION_OPTIONS.find(d => d.value === form.duration)?.label}</span>
            </div>
            {form.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                <span>{form.location}</span>
              </div>
            )}
            {form.notes && <p className="text-slate-500 text-xs mt-2">{form.notes}</p>}
          </div>
        </div>

        {/* Actions list */}
        <div>
          <p className="text-xs text-slate-500 mb-2 uppercase tracking-widest">O que será feito</p>
          <div className="space-y-2">
            {actions.map((a) => (
              <div key={a} className="flex items-center gap-2 text-sm text-slate-300">
                <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <Check className="h-2.5 w-2.5 text-emerald-400" />
                </div>
                {a}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-slate-800 flex gap-3">
        <button onClick={onBack} disabled={saving} className="flex-1 border border-slate-700 text-slate-300 font-semibold py-2.5 rounded-xl text-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
          ← Voltar
        </button>
        <button onClick={onConfirm} disabled={saving} className="flex-1 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-70 text-slate-950 font-bold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" />Salvando...</> : "Confirmar agendamento"}
        </button>
      </div>
    </div>
  );
}

function SuccessStep({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 p-8 text-center space-y-4">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
        <Check className="h-8 w-8 text-emerald-400" />
      </div>
      <div>
        <p className="text-lg font-bold text-white">Agendamento confirmado!</p>
        <p className="text-sm text-slate-400 mt-1">O evento foi salvo com sucesso.</p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        {[
          { label: "Salvo na agenda", color: "emerald" },
          { label: "Google Agenda", color: "cyan" },
          { label: "Prontuário atualizado", color: "violet" },
        ].map((b) => (
          <span key={b.label} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-${b.color}-500/20 text-${b.color}-300 border border-${b.color}-500/30`}>
            <Check className="h-3 w-3" />
            {b.label}
          </span>
        ))}
      </div>
      <button onClick={onClose} className="mt-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">
        Fechar
      </button>
    </div>
  );
}

// ─── Create Event Modal ───────────────────────────────────────────────────────

function CreateEventModal({ open, prefillDate, patients, uid, onClose, onCreated }: {
  open: boolean; prefillDate?: Date; patients: Patient[]; uid: string;
  onClose: () => void; onCreated: (apt?: Appointment) => void;
}) {
  const [step, setStep] = useState<ModalStep>(1);
  const [form, setForm] = useState<EventForm>(() => defaultForm(prefillDate));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm(defaultForm(prefillDate));
      setSaving(false);
    }
  }, [open, prefillDate]);

  const updateForm = (updates: Partial<EventForm>) => setForm((f) => ({ ...f, ...updates }));

  const handleSelectPatient = (p: Patient | null) => {
    if (!p) {
      updateForm({ patientId: null, patientName: "", patientDiagnosis: "", patientAlerts: [] });
    } else {
      const alerts = [
        ...(p.allergies || []),
        ...(p.comorbidities || []),
      ];
      updateForm({
        patientId: p.id,
        patientName: p.name,
        patientDiagnosis: p.diagnosis || p.anatomy || "",
        patientAlerts: alerts,
      });
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const [y, mo, d] = form.date.split("-").map(Number);
      const [h, mi] = form.time.split(":").map(Number);
      const start = new Date(y, mo - 1, d, h, mi);
      const end = new Date(start.getTime() + form.duration * 60000);

      const input: AppointmentInput = {
        patientId: form.patientId,
        patientName: form.patientName,
        patientDiagnosis: form.patientDiagnosis,
        patientAlerts: form.patientAlerts,
        type: form.type,
        startDate: start.getTime(),
        endDate: end.getTime(),
        location: form.location,
        notes: form.notes,
        color: form.color,
        origin: "orthoprecision",
        notifyPatient: form.notifyPatient,
        syncGoogle: form.syncGoogle,
        pendingGoogleSync: form.syncGoogle,
      };

      const newId = await createAppointment(uid, input);
      setStep("success");
      onCreated({ id: newId, ...input, doctorUid: uid } as Appointment);
    } catch (err) {
      console.error("Error creating appointment:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5">
          <h2 className="text-base font-bold text-white">
            {step === "success" ? "Sucesso!" : "Novo agendamento"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {step !== "success" && <StepIndicator step={step as 1 | 2 | 3} />}

        {step === 1 && (
          <PatientSearchStep
            patients={patients}
            form={form}
            onSelect={handleSelectPatient}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <TimeAndTypeStep
            form={form}
            onChange={updateForm}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <ReviewStep
            form={form}
            onBack={() => setStep(2)}
            onConfirm={handleConfirm}
            saving={saving}
          />
        )}
        {step === "success" && <SuccessStep onClose={onClose} />}
      </div>
    </div>
  );
}

// ─── Event Detail Modal ───────────────────────────────────────────────────────

function EventDetailModal({ apt, onClose, onDelete, onEdit, onNavigateToPatient }: {
  apt: Appointment | null;
  onClose: () => void;
  onDelete: (id: string) => void;
  onEdit: (apt: Appointment) => void;
  onNavigateToPatient: (patientId: string) => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  if (!apt) return null;
  const col = getEventColorStyle(apt.color);
  const displayNotes = getAppointmentDisplayNotes(apt);
  const isGoogleEvent = apt.origin === "google" || Boolean(apt.googleEventId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className={`h-1.5 ${col.dot}`} />
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className={`text-xs font-bold ${col.text}`}>{apt.type}</span>
              <h3 className="text-lg font-bold text-white mt-1">{apt.patientName}</h3>
              {apt.patientDiagnosis && <p className="text-sm text-slate-400">{apt.patientDiagnosis}</p>}
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Alerts */}
          {apt.patientAlerts && apt.patientAlerts.length > 0 && (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
              <p className="text-xs text-amber-200">{apt.patientAlerts.join(" · ")}</p>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 text-sm text-slate-300 mb-5">
            <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-500" /><span>{formatDate(apt.startDate)}</span></div>
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-500" /><span>{formatTime(apt.startDate)} – {formatTime(apt.endDate)} · {durationLabel(apt.startDate, apt.endDate)}</span></div>
            {apt.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-500" /><span>{apt.location}</span></div>}
            {isGoogleEvent ? (
              <p className="text-slate-500 text-xs mt-1 pl-6 whitespace-pre-line">
                {displayNotes || "Sem descrição"}
              </p>
            ) : (
              apt.notes && <p className="text-slate-500 text-xs mt-1 pl-6 whitespace-pre-line">{apt.notes}</p>
            )}
          </div>

          {/* Origin */}
          <div className="flex items-center gap-2 mb-5">
            {apt.origin === "google" ? <GoogleBadge /> : <span className="text-xs text-slate-600">OrthoPrecision</span>}
            {apt.syncGoogle && <span className="text-xs text-cyan-500 flex items-center gap-1"><RefreshCw className="h-3 w-3" />Sincronizado</span>}
          </div>

          {/* Actions */}
          {!confirmDelete ? (
            <div className="grid grid-cols-2 gap-2">
              {apt.patientId && (
                <button onClick={() => { onNavigateToPatient(apt.patientId!); onClose(); }}
                  className="flex items-center justify-center gap-1.5 text-xs text-slate-300 border border-slate-700 rounded-xl py-2 hover:bg-slate-800 transition-colors">
                  <ExternalLink className="h-3.5 w-3.5" />Prontuário
                </button>
              )}
              <button onClick={() => onEdit(apt)}
                className="flex items-center justify-center gap-1.5 text-xs text-slate-300 border border-slate-700 rounded-xl py-2 hover:bg-slate-800 transition-colors">
                <Pencil className="h-3.5 w-3.5" />Editar
              </button>
              <button onClick={() => setConfirmDelete(true)}
                className="flex items-center justify-center gap-1.5 text-xs text-red-400 border border-red-500/30 rounded-xl py-2 hover:bg-red-500/10 transition-colors">
                <Trash2 className="h-3.5 w-3.5" />Excluir
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-300 text-center">Confirmar exclusão?</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)} className="flex-1 border border-slate-700 text-slate-300 py-2 rounded-xl text-sm hover:bg-slate-800">Cancelar</button>
                <button onClick={() => { onDelete(apt.id); onClose(); }} className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded-xl text-sm">Confirmar</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Google Calendar Panel (sidebar) ─────────────────────────────────────────

function GoogleCalendarPanel({ status, onConnect, onDisconnect, onSync, syncing, connecting }: {
  status: GoogleCalendarStatus | null;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync: () => void;
  syncing: boolean;
  connecting: boolean;
}) {
  if (!status) return null;

  return (
    <div className="px-4 pb-4 space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Google Calendar</p>

      {status.connected ? (
        <>
          <div className="flex items-center gap-2 text-xs">
            <div className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
            <span className="text-emerald-300 truncate">{status.email}</span>
          </div>
          {status.syncError && (
            <p className="text-[10px] text-red-400 flex items-center gap-1">
              <WifiOff className="h-3 w-3" />
              Erro de sync
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onSync}
              disabled={syncing}
              className="flex-1 flex items-center justify-center gap-1.5 text-[11px] text-slate-300 border border-slate-700 rounded-lg py-1.5 hover:bg-slate-800 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </button>
            <button
              onClick={onDisconnect}
              className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
              title="Desconectar"
            >
              <Link2Off className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      ) : (
        <button
          onClick={onConnect}
          disabled={connecting}
          className="w-full flex items-center justify-center gap-2 border border-slate-700 rounded-xl py-2 text-xs text-slate-300 hover:bg-slate-800 hover:border-slate-600 disabled:opacity-50 transition-colors"
        >
          {connecting ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" />Conectando...</>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Conectar Google Agenda
            </>
          )}
        </button>
      )}
    </div>
  );
}

// ─── Main AgendaModule ────────────────────────────────────────────────────────

interface AgendaModuleProps {
  onNavigateToTab?: (tab: AppTab, patientId?: string) => void;
}

export default function AgendaModule({ onNavigateToTab }: AgendaModuleProps) {
  const { patients } = usePatient();
  const uid = auth.currentUser?.uid ?? "";

  // Navigation state
  const [view, setView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const today = useMemo(() => new Date(), []);

  // Data
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingApts, setLoadingApts] = useState(true);

  // UI state
  const [showCreate, setShowCreate] = useState(false);
  const [prefillDate, setPrefillDate] = useState<Date | undefined>();
  const [selectedApt, setSelectedApt] = useState<Appointment | null>(null);
  const [hoveredState, setHoveredState] = useState<{ apt: Appointment; rect: DOMRect } | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Google Calendar state
  const [gcStatus, setGcStatus] = useState<GoogleCalendarStatus | null>(null);
  const [gcConnecting, setGcConnecting] = useState(false);
  const [gcSyncing, setGcSyncing] = useState(false);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Range for Firestore query
  const { rangeStart, rangeEnd } = useMemo(() => {
    const start = startOfMonth(currentYear, currentMonth - 1);
    const end = endOfMonth(currentYear, currentMonth + 1);
    return { rangeStart: start.getTime(), rangeEnd: end.getTime() };
  }, [currentYear, currentMonth]);

  // Subscribe to appointments
  useEffect(() => {
    if (!uid) return;
    setLoadingApts(true);
    const unsub = subscribeToAppointments(uid, rangeStart, rangeEnd, (apts) => {
      setAppointments(apts);
      setLoadingApts(false);
    });
    return unsub;
  }, [uid, rangeStart, rangeEnd]);

  const navigate = useCallback((delta: number) => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") d.setMonth(d.getMonth() + delta);
      else if (view === "week") d.setDate(d.getDate() + delta * 7);
      else d.setDate(d.getDate() + delta);
      return d;
    });
  }, [view]);

  const headerTitle = useMemo(() => {
    if (view === "month") return `${MONTH_NAMES[currentMonth]} ${currentYear}`;
    if (view === "week") {
      const days = getWeekDays(currentDate);
      const first = days[0], last = days[6];
      return `${first.getDate()} – ${last.getDate()} ${MONTH_NAMES[last.getMonth()]} ${last.getFullYear()}`;
    }
    if (view === "day") return `${["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][currentDate.getDay()]}, ${currentDate.getDate()} ${MONTH_NAMES[currentMonth]}`;
    return "Lista";
  }, [view, currentDate, currentMonth, currentYear]);

  // Load Google Calendar connection status on mount
  useEffect(() => {
    if (!uid) return;
    fetchGoogleCalendarStatus()
      .then(setGcStatus)
      .catch(() => setGcStatus({ connected: false }));
  }, [uid]);

  const handleGcConnect = async () => {
    setGcConnecting(true);
    try {
      const email = await openGoogleOAuthPopup();
      setGcStatus({ connected: true, email, lastSyncAt: Date.now(), syncError: null });
    } catch (err: any) {
      alert(err?.message || "Erro ao conectar com o Google.");
    } finally {
      setGcConnecting(false);
    }
  };

  const handleGcDisconnect = async () => {
    if (!confirm("Desconectar o Google Calendar? Eventos do Google serão removidos da agenda.")) return;
    try {
      await disconnectGoogleCalendar();
      setGcStatus({ connected: false });
    } catch (err: any) {
      alert(err?.message || "Erro ao desconectar.");
    }
  };

  const handleGcSync = async () => {
    setGcSyncing(true);
    try {
      const { synced } = await syncGoogleCalendarNow();
      setGcStatus((prev) => prev ? { ...prev, lastSyncAt: Date.now(), syncError: null } : prev);
      console.log(`[GC] Synced ${synced} events`);
    } catch (err: any) {
      setGcStatus((prev) => prev ? { ...prev, syncError: err?.message } : prev);
    } finally {
      setGcSyncing(false);
    }
  };

  const handleDeleteApt = async (id: string) => {
    if (!uid) return;
    const apt = appointments.find(a => a.id === id);
    await deleteAppointment(uid, id);
    // Sync deletion to Google if applicable
    if (apt?.googleEventId && gcStatus?.connected) {
      await syncDeleteEvent(apt.googleEventId);
    }
  };

  /** Chip enters: delay 300ms before showing tooltip */
  const handleChipEnter = useCallback((apt: Appointment, rect: DOMRect) => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHoveredState({ apt, rect });
    }, 300);
  }, []);

  /** Chip leaves: give 150ms window for mouse to reach tooltip before closing */
  const handleChipLeave = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setHoveredState(null);
    }, 150);
  }, []);

  /** Mouse enters tooltip: cancel any pending close */
  const handleTooltipEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
  }, []);

  /** Mouse leaves tooltip: close immediately */
  const handleTooltipLeave = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setHoveredState(null);
  }, []);

  /** Lookup the full Patient object for the hovered appointment */
  const hoveredPatient = useMemo((): Patient | null => {
    if (!hoveredState?.apt.patientId) return null;
    return patients.find((p) => p.id === hoveredState.apt.patientId) ?? null;
  }, [hoveredState, patients]);

  const handleDayClick = (date: Date) => {
    if (view === "month") {
      setCurrentDate(date);
      setView("day");
    } else {
      setPrefillDate(date);
      setShowCreate(true);
    }
  };

  const handleNavigateToPatient = (patientId: string) => {
    onNavigateToTab?.(AppTab.PATIENTS, patientId);
  };

  // Visible appointments for current view
  const visibleApts = useMemo(() => {
    if (view === "month") {
      const grid = getMonthGrid(currentYear, currentMonth);
      const first = grid[0].getTime();
      const last = grid[grid.length - 1];
      last.setHours(23, 59, 59, 999);
      return appointments.filter((a) => a.startDate >= first && a.startDate <= last.getTime());
    }
    if (view === "week") {
      const days = getWeekDays(currentDate);
      const first = days[0].getTime();
      const last = new Date(days[6]);
      last.setHours(23, 59, 59, 999);
      return appointments.filter((a) => a.startDate >= first && a.startDate <= last.getTime());
    }
    if (view === "day") {
      return appointments.filter((a) => isSameDay(new Date(a.startDate), currentDate));
    }
    return appointments;
  }, [appointments, view, currentDate, currentYear, currentMonth]);

  return (
    <div className="flex h-full bg-slate-950 text-slate-100 overflow-hidden">
      {/* Sidebar */}
      {showSidebar && (
        <aside className="w-64 shrink-0 border-r border-slate-800 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <button
              onClick={() => { setPrefillDate(undefined); setShowCreate(true); }}
              className="w-full flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold py-2.5 rounded-xl text-sm transition-colors"
            >
              <Plus className="h-4 w-4" />
              Novo evento
            </button>
          </div>

          {/* Mini Calendar */}
          <MiniCalendar
            year={currentYear}
            month={currentMonth}
            selectedDate={currentDate}
            appointments={appointments}
            onDaySelect={(d) => { setCurrentDate(d); if (view === "month") setView("day"); }}
            onNavigate={(delta) => {
              setCurrentDate((prev) => {
                const d = new Date(prev);
                d.setMonth(d.getMonth() + delta);
                return d;
              });
            }}
          />

          {/* Upcoming events */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">Próximos</p>
            {appointments
              .filter((a) => a.startDate >= today.getTime())
              .slice(0, 5)
              .map((apt) => (
                <button
                  key={apt.id}
                  onClick={() => setSelectedApt(apt)}
                  className="w-full flex items-start gap-2 py-2 border-b border-slate-800/60 hover:bg-slate-900/40 transition-colors text-left"
                >
                  <ColorDot color={apt.color} size={6} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-300 truncate">{apt.patientName}</p>
                    <p className="text-[11px] text-slate-500">{formatDateShort(apt.startDate)} · {formatTime(apt.startDate)}</p>
                  </div>
                </button>
              ))}
          </div>

          {/* Google Calendar panel */}
          <div className="border-t border-slate-800 pt-3">
            <GoogleCalendarPanel
              status={gcStatus}
              onConnect={handleGcConnect}
              onDisconnect={handleGcDisconnect}
              onSync={handleGcSync}
              syncing={gcSyncing}
              connecting={gcConnecting}
            />
          </div>

          {/* Sync status footer */}
          <div className="px-4 pb-3 flex items-center gap-2 text-[11px]">
            {gcSyncing ? (
              <><RefreshCw className="h-3 w-3 animate-spin text-cyan-400" /><span className="text-cyan-400">Sincronizando...</span></>
            ) : gcStatus?.syncError ? (
              <><WifiOff className="h-3 w-3 text-red-400" /><span className="text-red-400">Erro de sincronização</span></>
            ) : gcStatus?.connected ? (
              <><Zap className="h-3 w-3 text-emerald-400" /><span className="text-slate-500">Google conectado</span></>
            ) : (
              <><RefreshCw className="h-3 w-3 text-slate-600" /><span className="text-slate-600">Google não conectado</span></>
            )}
          </div>
        </aside>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Calendar header */}
        <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 shrink-0">
          <button
            onClick={() => setShowSidebar((v) => !v)}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 lg:hidden"
          >
            <List className="h-4 w-4" />
          </button>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => navigate(1)} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <button
            onClick={() => { setCurrentDate(new Date()); }}
            className="text-xs text-slate-400 border border-slate-700 px-3 py-1 rounded-lg hover:bg-slate-800 transition-colors"
          >
            Hoje
          </button>

          <h2 className="text-sm font-semibold text-white ml-2 flex-1 truncate">{headerTitle}</h2>

          {loadingApts && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}

          {/* View selector */}
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-xl p-1">
            {([
              { id: "month", icon: Grid3X3, label: "Mês" },
              { id: "week",  icon: Columns3, label: "Semana" },
              { id: "day",   icon: CalendarDays, label: "Dia" },
              { id: "list",  icon: List, label: "Lista" },
            ] as const).map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                title={label}
                className={`p-1.5 rounded-lg transition-colors ${view === id ? "bg-slate-700 text-white" : "text-slate-500 hover:text-slate-300"}`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>

          <button
            onClick={() => { setPrefillDate(undefined); setShowCreate(true); }}
            className="flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-3 py-1.5 rounded-xl text-sm transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Novo</span>
          </button>
        </header>

        {/* Calendar view */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {view === "month" && (
            <MonthView
              year={currentYear}
              month={currentMonth}
              today={today}
              appointments={visibleApts}
              onDayClick={handleDayClick}
              onEventClick={setSelectedApt}
              onChipEnter={handleChipEnter}
              onChipLeave={handleChipLeave}
            />
          )}
          {view === "week" && (
            <WeekView
              refDate={currentDate}
              today={today}
              appointments={visibleApts}
              onSlotClick={(d) => { setPrefillDate(d); setShowCreate(true); }}
              onEventClick={setSelectedApt}
              onChipEnter={handleChipEnter}
              onChipLeave={handleChipLeave}
            />
          )}
          {view === "day" && (
            <DayView
              date={currentDate}
              today={today}
              appointments={visibleApts}
              onSlotClick={(d) => { setPrefillDate(d); setShowCreate(true); }}
              onEventClick={setSelectedApt}
              onChipEnter={handleChipEnter}
              onChipLeave={handleChipLeave}
            />
          )}
          {view === "list" && (
            <ListView
              appointments={visibleApts}
              onEventClick={setSelectedApt}
            />
          )}
        </div>
      </div>

      {/* Hover tooltip — rendered outside calendar flow to avoid z-index issues */}
      {hoveredState && (
        <EventTooltip
          apt={hoveredState.apt}
          rect={hoveredState.rect}
          patient={hoveredPatient}
          onTooltipEnter={handleTooltipEnter}
          onTooltipLeave={handleTooltipLeave}
          onNavigateToPatient={(id) => { handleTooltipLeave(); handleNavigateToPatient(id); }}
        />
      )}

      {/* Modals */}
      <CreateEventModal
        open={showCreate}
        prefillDate={prefillDate}
        patients={patients}
        uid={uid}
        onClose={() => setShowCreate(false)}
        onCreated={(apt) => {
          // Sync to Google Calendar after local save (non-blocking)
          if (apt?.syncGoogle && gcStatus?.connected && apt.id) {
            syncCreateEvent({
              appointmentId: apt.id,
              summary: `${apt.type} — ${apt.patientName}`,
              description: [
                apt.patientDiagnosis && `Diagnóstico: ${apt.patientDiagnosis}`,
                apt.notes && `Obs: ${apt.notes}`,
              ].filter(Boolean).join("\n"),
              location: apt.location || "",
              start: apt.startDate,
              end: apt.endDate,
            });
          }
        }}
      />

      <EventDetailModal
        apt={selectedApt}
        onClose={() => setSelectedApt(null)}
        onDelete={handleDeleteApt}
        onEdit={(apt) => {
          setSelectedApt(null);
          // TODO: pre-populate the create modal for editing
        }}
        onNavigateToPatient={handleNavigateToPatient}
      />
    </div>
  );
}
